import { QueryClient, focusManager, onlineManager } from '@tanstack/react-query'
import { initSyncEngine, processQueue } from '../utils/syncEngine'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // Don't retry 404s or explicit permission errors
        if (error?.code === 'PGRST116') return false // row not found
        // Retry AbortErrors (auth token refresh mid-request) and
        // transient network/server errors up to 3 times
        return failureCount < 3
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
      refetchOnMount: true,
    }
  }
})

// --- App Resume / Focus handling ---
// On native (Capacitor), the standard visibilitychange/focus events don't fire
// reliably. Use Capacitor's appStateChange to tell React Query the app resumed.
// On web, visibilitychange works fine and React Query handles it by default.

const isNative = typeof window !== 'undefined' &&
  window.Capacitor?.isNativePlatform?.()

if (isNative) {
  // Disable React Query's default browser focus detection — we'll drive it manually
  focusManager.setEventListener(() => {
    // Return a no-op unsubscribe; we manage focus via Capacitor below
    return () => {}
  })

  import('@capacitor/app').then(({ App }) => {
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        // Delay the focus signal so the WebView re-composites its cached
        // snapshot first — avoids a visible flash where React re-renders
        // while the GPU layer is still blank.
        setTimeout(() => {
          focusManager.setFocused(true)
        }, 100)

        // Drain any pending scores from the offline queue
        processQueue()

        // Force Supabase realtime to reconnect stale WebSocket channels,
        // then audit them 5 s later — if any channel that existed pre-pause
        // is not joined again, fire a Sentry capture so we can see how often
        // realtime fails to recover on resume.
        import('./supabase').then(({ supabase }) => {
          const preChannels = supabase.getChannels?.() || []
          const preTopics = preChannels.map((c) => c.topic).filter(Boolean)
          supabase.realtime.connect()
          if (preTopics.length === 0) return
          setTimeout(() => {
            try {
              const post = supabase.getChannels?.() || []
              const postState = post.reduce((acc, c) => {
                if (c.topic) acc[c.topic] = c.state
                return acc
              }, {})
              const stuck = preTopics.filter((t) => postState[t] !== 'joined')
              if (stuck.length > 0) {
                import('../utils/logger').then(({ default: logger }) => {
                  logger.capture(new Error('Realtime channels not joined 5s after resume'), {
                    stuckTopics: stuck, postState, preCount: preTopics.length,
                    isOnline: navigator.onLine,
                  })
                }).catch(() => {})
              }
            } catch { /* ignore */ }
          }, 5000)
        }).catch(() => {})
      } else {
        focusManager.setFocused(false)
      }
    })
  }).catch(() => {
    // @capacitor/app not available — fall back to visibilitychange
    document.addEventListener('visibilitychange', () => {
      focusManager.setFocused(document.visibilityState === 'visible')
    })
  })

  // Use Capacitor Network plugin to detect connectivity changes
  import('@capacitor/network').then(({ Network }) => {
    Network.addListener('networkStatusChange', ({ connected, connectionType }) => {
      // Lazy import to avoid circular dependency at module load time
      import('../utils/logger').then(({ default: logger }) => {
        logger.network('Connectivity changed', { connected, type: connectionType })
      }).catch(() => {})
      onlineManager.setOnline(connected)
    })
  }).catch(() => {
    // @capacitor/network not available — React Query's default online detection is fine
  })
}

// Start the offline score sync engine
initSyncEngine()
